import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { makeGetCurrentUser } from '../selectors/users';
import { makeGetCustomFields } from '../selectors/custom-fields';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';
import { makeGetTimeEntriesByTaskId } from '../selectors/time-tracking';

import { fetchCustomFields, fetchCustomFieldsValues } from '../actions/custom-fields';
import { fetchTimeEntries } from '../actions/time-entries';

import { featureAvailable } from '../services/features-service';
import { getTimeEntriesDuration } from '../utils/time-tracking';

import CustomField from './custom-field';
import CustomFieldEditor from './custom-field-editor';

import CustomFieldPlaceholder from './placeholders/custom-field.placeholder';

class CustomFields extends PureComponent {
    static defaultProps = {
        type: '',
        values: []
    };

    state = {
        editingField: null,
        editing: false
    }

    handleEditorComplete = () => {
        this.setState({
            editing: false,
            editingField: null
        });
    }

    handleEditorBack = () => {
        this.setState({
            editing: false,
            editingField: null
        });
    }

    handleEdit = editingField => {
        this.setState({
            editingField,
            editing: true
        });
    }

    attemptFetch() {
        if (this.props.fetched || this.props.loading) {
            return;
        }

        fetchCustomFields(this.props.projectId);
    }

    attemtFetchValues() {
        if (this.props.values || !this.props.associationId) {
            return;
        }

        fetchCustomFieldsValues(this.props.association, this.props.associationId);
    }

    attemptFetchTimeTracking() {
        if (this.props.association !== 'task' || !featureAvailable('timeTracking')) {
            return;
        }

        if (this.props.customFields.some(field => field.type === 'budget') && !this.props.timeEntriesFetched && !this.props.timeEntriesLoading) {
            fetchTimeEntries({
                taskId: this.props.associationId
            });
        }
    }

    showEditor() {
        this.setState({
            editing: true
        }, () => {
            this.element && this.element.scrollIntoView();
        });
    }

    setElement = el => this.element = el;

    componentDidMount() {
        this.attemptFetch();
        this.attemtFetchValues();
        this.attemptFetchTimeTracking();

        this.props.provideController && this.props.provideController({
            create: () => {
                this.showEditor();
            }
        });
    }

    componentDidUpdate() {
        this.attemptFetch();
        this.attemptFetchTimeTracking();
    }

    componentWillUnmount() {
        this.props.provideController && this.props.provideController(null);
    }

    render() {
        const { t } = this.props;

        const customFields = this.props.customFields.map(field => <CustomField
            key={field.id}
            field={field}
            id={field.id}
            type={field.type}
            name={field.name}
            hidden={field.hidden}
            value={(this.props.values.find(({ id }) => id === field.id) || {}).value}
            currency={field.currency}
            options={field.options}
            costPerHour={field.cost_per_hour}
            trackingType={field.tracking_type}
            trackedTime={this.props.trackedTime}
            onEdit={this.handleEdit}
            onChange={this.props.onChange}
        />);

        if (this.props.noContainers) {
            return customFields;
        }

        if (!this.props.customFields.length && this.props.fetched && !this.state.editing) {
            return (
                <p className="dimmed-text">{ t('fieldsDescription') }</p>
            )
        }

        return (
            <div className="custom-fields-holder" ref={this.setElement}>
                {
                     this.state.editing ? (
                        <CustomFieldEditor
                            fieldId={this.state.editingField?.id}
                            type={this.state.editingField?.type}
                            name={this.state.editingField?.name}
                            hideFromGuests={this.state.editingField?.hidden?.includes('client')}
                            currency={this.state.editingField?.currency}
                            options={this.state.editingField?.options}
                            costPerHour={(this.state.editingField?.cost_per_hour || 0) - 0}
                            trackingType={this.state.editingField?.tracking_type}
                            association={this.props.association}
                            associationId={this.props.associationId}
                            projectId={this.props.projectId}
                            trackedTime={this.props.trackedTime}
                            onComplete={this.handleEditorComplete}
                            onBack={this.handleEditorBack}
                        />
                    ) : (
                        <div className={'custom-fields ' + this.props.type}>
                            {
                                !this.props.fetched && this.props.loading ? (
                                    <React.Fragment>
                                        <CustomFieldPlaceholder />

                                        <CustomFieldPlaceholder />

                                        <CustomFieldPlaceholder />
                                    </React.Fragment>
                                ) : null
                            }

                            { customFields }
                        </div>
                    )
                }
            </div>
        );
    }
}

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getCustomFields = makeGetCustomFields();
    const getTimeEntriesByTaskId = makeGetTimeEntriesByTaskId();
    const getCustomFieldsFetched = makeGetFetchedState();
    const getCustomFieldsLoading = makeGetLoadingState();
    const getTimeEntriesFetched = makeGetFetchedState();
    const getTimeEntriesLoading = makeGetLoadingState();

    return ({ users, customFields, timeTracking }, ownProps) => {
        const currentUser = getCurrentUser(users);
        const timeEntries = ownProps.association === 'task' && getTimeEntriesByTaskId(timeTracking.items, ownProps.associationId);
        const trackedTime = timeEntries && timeEntries.length && getTimeEntriesDuration(timeEntries);

        return {
            customFields: getCustomFields(customFields.items, currentUser?.role, ownProps.association, ownProps.projectId),
            trackedTime,
            fetched: ownProps.projectId ? getCustomFieldsFetched(customFields.fetchInfo, 'project', ownProps.projectId) : true,
            loading: ownProps.projectId ? getCustomFieldsLoading(customFields.fetchInfo, 'project', ownProps.projectId) : false,
            timeEntriesFetched: ownProps.association === 'task' && ownProps.associationId ? getTimeEntriesFetched(timeTracking.fetchInfo, 'task', ownProps.associationId) : true,
            timeEntriesLoading: ownProps.association === 'task' && ownProps.associationId ? getTimeEntriesLoading(timeTracking.fetchInfo, 'task', ownProps.associationId) : false,
        }
    }
};

export default connect(makeMapStateToProps)(withTranslation('fields')(CustomFields));
